import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useTheme } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { mapMarkers, selectedGeocodeLocation } from '@/common/atoms/community';
import { useGeocoder } from '@/views/Community/views/CommunityListings/CommunityListings.helpers';
import type { TeamSearchResult } from '../../types';
import { GroupMarker } from '../GroupMarker';
import { ClusterRenderer } from '../MapRenderer/ClusterRenderer';
import { Marker } from './Marker';

interface Props {
  map: google.maps.Map | null;
  partnerGroups: Record<string, TeamSearchResult[]>;
}

export const Markers = ({ map, partnerGroups }: Props) => {
  const theme = useTheme();
  const clusterer = useRef<MarkerClusterer | null>(null);
  const selectedLocation = useAtomValue(selectedGeocodeLocation);
  const [markers, setMarkers] = useAtom(mapMarkers);
  const { geocode } = useGeocoder();

  // Initialize MarkerClusterer
  useEffect(() => {
    if (!map) return;

    clusterer.current ??= new MarkerClusterer({
      map,
      renderer: new ClusterRenderer(theme, () => selectedLocation, geocode),
    });
  }, [map, theme, selectedLocation, geocode]);

  // Update markers
  useEffect(() => {
    clusterer.current?.clearMarkers(true);
    clusterer.current?.addMarkers(Object.values(markers));
  }, [markers]);

  const setMarker = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement | null, key: string) => {
      setMarkers((prev) => {
        if (marker) {
          return { ...prev, [key]: marker };
        }
        const newMarkers = { ...prev };
        delete newMarkers[key];
        return newMarkers;
      });
    },
    [setMarkers],
  );

  return (
    <>
      {Object.entries(partnerGroups).map(([key, group]) =>
        group.length > 1 ? (
          <GroupMarker
            setMarker={setMarker}
            key={key}
            partners={group}
            groupKey={key}
          />
        ) : (
          <Marker setMarker={setMarker} key={key} partner={group[0]} />
        ),
      )}
    </>
  );
};
